import { useContext } from "react";

import MaskService from "../../../utils/mask/maskService";
import { UserContext } from "../../../context/UserContext";
import { CustomerContext } from "../contexts/CustomerContext";
import { notify } from "../../../utils/notify";
import validatesNotNull from "../../../utils/validatesNotNull";
import { postCustomer, putCustomer } from "../../../repositories/v1/customer";
import { postLog } from "../../../repositories/v1/log";
import { getCustomerByCnpjPlug } from "../../../repositories/clientRepository";
import {
  handleDocumentInfosByBigData,
  handleAddressInfosByBigData,
} from "../../../services/infosByBigData";
import { GlobalVariables } from "../../../global";

export default function useCustomerController() {
  const { user } = useContext(UserContext);
  const {
    customerExists,
    setCustomerExists,
    documentType,
    setDocumentType,
    reason,
    setReason,
    fantasy,
    setFantasy,
    cep,
    setCep,
    uf,
    setUf,
    county,
    setCounty,
    address,
    setAddress,
    number,
    setNumber,
    district,
    setDistrict,
    telephone,
    setTelephone,
    cnpj,
    setCnpj,
    cnpjMatriz,
    setCnpjMatriz,
    ie,
    setIe,
    im,
    setIm,
    complement,
    setComplement,
    codIbge,
    setCodIbge,
    email,
    setEmail,
    listSegments,
    segment,
    setSegment,
    checkMatriz,
    checkAccountant,
    accountantCustomer,
    setHasCertificate,
    setPasswordCertificate,
    setLogomarca,
    setPromocode,
    setConsultantCompany,
    setConsultantUser,
    setFranchisedCompany,
    setFranchisedUser,
    setAccountantCompany,
    setAccountantUser,
    setEntityCompany,
    setEntityUser,
    setProducts,
    setErrors,
    loadings,
    setLoadings,
    birthDate,
    setBirthDate,
  } = useContext(CustomerContext);

  const handleChangeCep = async (e, findHub = true) => {
    try {
      const cepRemoveMask = e.target.value.replace(/[^0-9]+/g, "");
      setCep(MaskService.toMask("cep", e.target.value));

      if (
        !findHub ||
        cepRemoveMask === "00000000" ||
        cepRemoveMask.length !== 8
      ) {
        return;
      }

      setLoadings((current) => ({ ...current, screen: true }));

      await handleAddressInfosByBigData({
        postalCode: cepRemoveMask,
        setDistrict,
        setCounty,
        setAddress,
        setUf,
        setCodIbge,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoadings((current) => ({ ...current, screen: false }));
    }
  };

  const handleSaveCustomer = async () => {
    try {
      if (Object.keys(loadings).find((key) => loadings[key])) {
        return notify("Aguarde todos os campos serem carregados.");
      }

      setLoadings((current) => ({ ...current, screen: true }));

      let validationFail = false;

      const requiredInputs = [
        { value: cnpj, key: "cnpj" },
        { value: fantasy, key: "fantasy" },
        { value: reason, key: "reason" },
        { value: cep, key: "cep" },
        { value: uf, key: "uf" },
        { value: county, key: "county" },
        { value: address, key: "address" },
        { value: district, key: "district" },
        { value: telephone, key: "telephone" },
        { value: email, key: "email" },
      ];

      if (checkMatriz) {
        requiredInputs.push({ value: cnpjMatriz, key: "cnpjMatriz" });
      }

      const resultValidation = validatesNotNull(requiredInputs);

      if (
        cnpj &&
        documentType === "cnpj" &&
        !MaskService.isValid("cnpj", MaskService.toMask("cnpj", cnpj))
      ) {
        validationFail = true;
        resultValidation.errorsValidation.cnpj = true;
        notify("O CNPJ informado é inválido.", true, "error");
      }

      if (
        cnpj &&
        documentType === "cpf" &&
        !MaskService.isValid("cpf", MaskService.toMask("cpf", cnpj))
      ) {
        validationFail = true;
        resultValidation.errorsValidation.cnpj = true;
        notify("O CPF informado é inválido.", true, "error");
      }

      if (cep && MaskService.toMask("cep", cep).length !== 10) {
        validationFail = true;
        resultValidation.errorsValidation.cep = true;
        notify("O CEP informado é inválido.", true, "error");
      }

      if (
        telephone &&
        !MaskService.isValid(
          "cel-phone",
          MaskService.toMask("cel-phone", telephone)
        )
      ) {
        validationFail = true;
        resultValidation.errorsValidation.telephone = true;
        notify("O telefone informado é inválido.", true, "error");
      }

      if (
        checkMatriz &&
        cnpjMatriz &&
        !MaskService.isValid("cnpj", MaskService.toMask("cnpj", cnpjMatriz))
      ) {
        validationFail = true;
        resultValidation.errorsValidation.cnpjMatriz = true;
        notify("O CNPJ da matriz informado é inválido.", true, "error");
      }

      setErrors(resultValidation.errorsValidation);
      if (resultValidation.error || validationFail) return;

      const data = {
        IdSegmento: segment?.Id || GlobalVariables.SEGMENTS.DEFAULT,
        Fantasia: fantasy,
        Razao: reason,
        CNPJ: cnpj.replace(/[^0-9]+/g, ""),
        MatrizCNPJ: checkMatriz ? cnpjMatriz : null,
        CEP: cep.replace(/[^0-9]+/g, ""),
        UF: uf,
        Municipio: county,
        Endereco: address,
        Numero: number.toString().replace(/[^0-9]+/g, "") || null,
        Bairro: district,
        Complemento: complement,
        CodigoCidadeIBGE: codIbge || "",
        Telefone: telephone.replace(/[^0-9]+/g, ""),
        IE: ie,
        IM: im,
        Email: email.replace(/ /g, ""),
        ContadorCNPJ:
          (accountantCustomer?.CNPJ || "").replace(/[^0-9]+/g, "") || null,
        FlagContador: checkAccountant,
      };

      let result = {};
      let text = "";

      if (customerExists) {
        result = await putCustomer({
          body: data,
          params: "?qyonHubPost=true",
          cnpj: cnpj.replace(/[^0-9]+/g, ""),
        });

        text = "editado";
      } else {
        result = await postCustomer({
          body: data,
          params: "?qyonHubPost=true",
        });

        text = "criado";
      }

      if (result?.data?.success) {
        postLog({
          EmailUsuario: user?.Email,
          Descricao: `${customerExists ? "edição" : "cadastro"} de cliente(${
            data.CNPJ
          })`,
          Origem: "plug",
          Tabela: "clientes",
          Dados: JSON.stringify(data),
        });
        notify(`Cliente ${text} com sucesso.`, true, "info");
        setCustomerExists(true);
      }
    } catch (error) {
      console.log(error);
      notify("Não foi possível salvar o cliente.", true);
    } finally {
      setLoadings((current) => ({ ...current, screen: false }));
    }
  };

  const handleBirthDate = async (birthDate) => {
    if (birthDate.getFullYear() < 1900 || !cnpj) {
      return;
    }

    setBirthDate(birthDate);

    await handleDocumentInfosByBigData({
      type: documentType,
      document: cnpj.replace(/[^0-9]+/g, ""),
      birthDate: birthDate.toISOString().split("T")[0],
      setters: {
        setFantasy,
        setReason,
      },
    });
  };

  const handleChangeCnpjCpf = async (e) => {
    if (customerExists) return;

    const valueWithoutMask = e.target.value.replace(/[^0-9]+/g, "");

    setCnpj(MaskService.toMask(documentType, e.target.value));

    if (documentType === "cpf") {
      if (!birthDate) return;
      if (valueWithoutMask.length) setBirthDate("");
    }

    const documentTypeProps = {
      cnpj: {
        length: 14,
        setters: {
          setTelephone,
          setFantasy,
          setReason,
          setNumber,
          setState: setUf,
          setAddress,
          setCity: setCounty,
          setDistrict,
          setCep,
          setEmail,
        },
      },
      cpf: { length: 11, setters: setFantasy, setReason },
    };

    if (valueWithoutMask.length === documentTypeProps[documentType].length) {
      try {
        document.activeElement.blur();
        setLoadings((current) => ({ ...current, screen: true }));
        clearCustomerContext(true);

        const clientExists = await getCustomerByCnpjPlug({
          clientCnpj: valueWithoutMask,
        });

        if (clientExists?.data) {
          setCnpj("");
          setBirthDate("");
          return notify("Este cliente já está cadastrado.");
        }

        await handleDocumentInfosByBigData({
          type: documentType,
          document: valueWithoutMask,
          birthDate: documentType === "cpf" ? birthDate : null,
          setters: documentTypeProps[documentType].setters,
        });
      } catch (error) {
        console.error(error);
      } finally {
        setLoadings((current) => ({ ...current, screen: false }));
      }
    }
  };

  const clearCustomerContext = (ignoreCnpj) => {
    if (!ignoreCnpj) {
      setCnpj("");
    }
    setReason("");
    setFantasy("");
    setBirthDate("");
    setCep("");
    setUf("");
    setCounty("");
    setAddress("");
    setNumber("");
    setDistrict("");
    setTelephone("");
    setCnpjMatriz("");
    setIe("");
    setIm("");
    setComplement("");
    setCodIbge("");
    setEmail("");
    setSegment(listSegments.find((s) => s.Id === GlobalVariables.SEGMENTS.DEFAULT));
    setHasCertificate(false);
    setPasswordCertificate("");
    setLogomarca("");

    setPromocode({});
    setConsultantCompany(null);
    setConsultantUser(null);
    setFranchisedCompany(null);
    setFranchisedUser(null);
    setAccountantCompany(null);
    setAccountantUser(null);
    setEntityCompany(null);
    setEntityUser(null);

    setProducts([
      {
        Id: 0,
        IdFamilia: 0,
        IdProduto: 0,
        IdVersao: 0,
        Rota: "",
        IdTipoPromotor: 0,
        PromotorCNPJ: "",
        IdUsuario: 0,
        DataExpiracao: "",
        DataContrato: "",
        DataCancelamento: "",
        DataBloqueio: "",
        DataConsultaLimite: "",
        Demo: false,
        Trial: false,
        Freemium: false,
        ValoresParametros: {},
        IdMotivoCancelamento: 0,
      },
    ]);

    setErrors({});
  };

  const handleChangeDocumentType = (e) => {
    if (customerExists) return;

    clearCustomerContext();

    setDocumentType(e.target.value);
  };

  return {
    handleChangeCep,
    handleSaveCustomer,
    handleChangeCnpjCpf,
    clearCustomerContext,
    handleChangeDocumentType,
    handleBirthDate,
  };
}
